
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import PinnedIcon from './images/pinned.vue.svg';
import CommentIcon from './images/preview-comment.vue.svg';
import DownvoteIcon from './images/preview-downvote.vue.svg';
import UpvoteIcon from './images/preview-upvote.vue.svg';
import WarRoomPostActions from '@/components/partials/war-room/WarRoomPostActions.vue';
import WarRoomPostPreviewImage from '@/components/partials/war-room/WarRoomPostPreviewImage/WarRoomPostPreviewImage.vue';
import type { ICommenter, IPost, IPostCategory } from '@/types/community';
import WarRoomCategoryBadge from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadge.vue';
import WarRoomCategoryBadgeTopic from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadgeTopic.vue';
import WarRoomPostPollV2 from '@/components/partials/war-room/WarRoomPostPollV2/WarRoomPostPollV2.vue';
import WarRoomExposedComments from '@/components/partials/war-room/WarRoomExposedComments/WarRoomExposedComments.vue';
import WarRoomMembersOnlyLabel from '~/components/partials/war-room/WarRoomMembersOnlyLabel/WarRoomMembersOnlyLabel.vue';
import { authorTypes } from '~/components/partials/war-room/constants';
import warRoomSignUpMixin from '~/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import { categoryNames } from '~/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import DealStoryBadge from '~/components/partials/war-room/WarRoomDeal/DealStoryBadge.vue';
import WarRoomDealStoryHeaderFormatter from '~/components/partials/war-room/WarRoomDeal/WarRoomDealStoryHeaderFormatter.vue';
import WarRoomAvatar from '~/components/partials/war-room/WarRoomAvatar.vue';
import { isPostContentADealStory } from '~/types/typeHelpers';

export default Vue.extend({
  name: 'WarRoomPostPreviewPresenter',
  components: {
    WarRoomAvatar,
    WarRoomDealStoryHeaderFormatter,
    DealStoryBadge,
    UpvoteIcon,
    DownvoteIcon,
    CommentIcon,
    BravadoText,
    WarRoomPostActions,
    WarRoomPostPreviewImage,
    PinnedIcon,
    WarRoomCategoryBadge,
    WarRoomCategoryBadgeTopic,
    WarRoomPostPollV2,
    WarRoomExposedComments,
    WarRoomMembersOnlyLabel,
  },
  mixins: [warRoomSignUpMixin],
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<IPost>,
  },
  computed: {
    ...mapGetters('warRoom', ['isAdmin']),
    titleClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__title': true,
        'war-room-post-preview-presenter__title--deal': this.isDealStory,
      };
    },
    showExposedComments(): boolean {
      if (this.post.poll) return false;

      return !!Object.keys(this.post.popular_comments?.comment_dict || {}).length;
    },
    link(): string {
      return `${this.$pages.POSTS_PAGE.path}/${this.post.slug}`;
    },
    isUpvoted(): boolean {
      return this.post.current_vote === 1;
    },
    isDownvoted(): boolean {
      return this.post.current_vote === -1;
    },
    ratingClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rating': true,
        'war-room-post-preview-presenter__rating--upvoted': this.isUpvoted,
        'war-room-post-preview-presenter__rating--downvoted': this.isDownvoted,
      };
    },
    upvoteClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rate': true,
        'war-room-post-preview-presenter__rate--upvoted': this.isUpvoted,
      };
    },
    downvoteClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rate': true,
        'war-room-post-preview-presenter__rate--downvoted': this.isDownvoted,
      };
    },
    hasPreviewImage(): boolean {
      return Boolean(this.post.cover_url);
    },
    isUnreadIconVisible(): boolean {
      if (this.post.pinned) return false;
      if (!this.$auth.loggedIn) return false;
      if (this.isDealStory) return false;
      if (this.$auth.user.username === this.post.user_username) return false;

      return this.post.viewed === false;
    },
    pollClasses(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__poll': true,
        'war-room-post-preview-presenter__poll--not-logged': !this.$auth.loggedIn,
      };
    },
    isDealStory(): boolean {
      return (this.post.category as IPostCategory).slug === categoryNames.WIN_WIRE;
    },
    commentsButtonText(): string | number {
      if (this.isDealStory) {
        return 'Say congrats!';
      } else if (!this.post.comment_count.total) {
        return 'Comment';
      } else {
        return this.post.comment_count.total;
      }
    },
    commentsButtonLink(): string {
      const linkParams = ['comment=true'];
      if (this.isDealStory) linkParams.push('mention=true');

      const link = `${this.link}?${linkParams.join('&')}`;

      return this.$auth.loggedIn ? link : `?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`;
    },
    dealCompanyName(): string | undefined {
      if (isPostContentADealStory(this.post.content.data) && this.post.content.data.deal.company) {
        return this.post.content.data.deal.company.name;
      } else {
        return undefined;
      }
    },
    dealDepartments(): string[] {
      if (isPostContentADealStory(this.post.content.data) && this.post.content.data.deal.departments) {
        return this.post.content.data.deal.departments.map(d => d.name);
      } else {
        return [];
      }
    },
    dealAuthorAvatar(): string {
      return isPostContentADealStory(this.post.content.data)
        ? this.post.content.data.deal.user.war_room_avatar_url
        : '';
    },
  },
  methods: {
    isPublicCommenter(commenter: ICommenter): boolean {
      return commenter.type === authorTypes.PUBLIC;
    },
    onUpvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isUpvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', 1);
      }
    },
    onDownvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isDownvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', -1);
      }
    },
  },
});
